import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, RoleBadge, EligibilityBadge, Loading, Modal, inputCls, labelCls, btnPrimary, btnGhost, roleMeta } from "@/components/kit";
import { Users, Trash, PencilSimple, UserPlus, LinkSimple, Copy, CheckCircle, Prohibit, X, Hourglass, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["athlete", "coach", "teacher", "parent", "athletic_director"];

export default function People() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [pending, setPending] = useState([]);
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [approve, setApprove] = useState(null); // { user, child_ids, team_ids, sport }
  const [resetResult, setResetResult] = useState(null);
  const [resetCopied, setResetCopied] = useState(false);

  const regLink = `${window.location.origin}/register/${user?.school_id}`;

  const load = async () => {
    const [u, t, p] = await Promise.all([api.get("/users"), api.get("/teams"), api.get("/pending-users")]);
    setUsers(u.data);
    setTeams(t.data);
    setAthletes(u.data.filter((x) => x.role === "athlete"));
    setPending(p.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ id: null, name: "", email: "", password: "demo1234", role: "athlete", sport: "", team_ids: [], child_ids: [], student_ids: [] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setForm({ id: u.id, name: u.name, email: u.email, password: "", role: u.role, sport: u.sport || "", team_ids: u.team_ids || [], child_ids: u.child_ids || [], student_ids: u.student_ids || [] });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/users/${form.id}`, { name: form.name, sport: form.sport, role: form.role, team_ids: form.team_ids, child_ids: form.child_ids, student_ids: form.student_ids });
        toast.success("User updated");
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role, sport: form.sport, team_ids: form.team_ids, child_ids: form.child_ids, student_ids: form.student_ids });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Removed");
    load();
  };

  const toggleEligibility = async (u) => {
    const makeIneligible = u.eligible !== false;
    let reason = null;
    if (makeIneligible) {
      reason = window.prompt("Reason for marking ineligible (optional):", "");
      if (reason === null) return;
    }
    try {
      await api.put(`/users/${u.id}/eligibility`, { eligible: !makeIneligible, reason });
      toast.success(makeIneligible ? "Marked ineligible" : "Marked eligible");
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  const toggle = (field, val) => {
    const arr = form[field].includes(val) ? form[field].filter((x) => x !== val) : [...form[field], val];
    setForm({ ...form, [field]: arr });
  };

  const copyLink = () => { navigator.clipboard.writeText(regLink); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s password? A temporary password will be generated and they'll be required to change it on next login.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/reset-password`, {});
      setResetResult(r.data);
      setResetCopied(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };
  const copyTempPw = () => { navigator.clipboard.writeText(resetResult.temp_password); setResetCopied(true); toast.success("Temporary password copied"); setTimeout(() => setResetCopied(false), 2000); };

  const openApprove = (u) => setApprove({ user: u, child_ids: [], team_ids: u.team_ids || [], sport: u.sport || "" });
  const toggleApprove = (field, val) => {
    const arr = approve[field].includes(val) ? approve[field].filter((x) => x !== val) : [...approve[field], val];
    setApprove({ ...approve, [field]: arr });
  };
  const confirmApprove = async () => {
    try {
      await api.post(`/users/${approve.user.id}/approve`, { child_ids: approve.child_ids, team_ids: approve.team_ids, sport: approve.sport || null });
      toast.success(`${approve.user.name} approved`);
      setApprove(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };
  const reject = async (u) => {
    if (!window.confirm(`Reject and delete ${u.name}'s request?`)) return;
    try {
      await api.post(`/users/${u.id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error("Could not reject"); }
  };

  if (!users) return <Loading />;
  const filtered = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-6xl mx-auto">
      <PageHeader eyebrow="Manage" title="People" accent="hub" icon={Users}>
        <div className="flex gap-2">
          <button onClick={() => setLinkOpen(true)} className={btnGhost} data-testid="registration-link-button"><LinkSimple size={18} weight="bold" /> Registration Link</button>
          <button onClick={openCreate} className={btnPrimary} data-testid="add-user-button"><UserPlus size={18} weight="bold" /> Add Person</button>
        </div>
      </PageHeader>

      {pending.length > 0 && (
        <Card className="mb-6 overflow-hidden" >
          <div className="flex items-center gap-2 px-4 md:px-6 py-4 border-b border-white/10 bg-[#FF3B30]/5">
            <Hourglass size={18} weight="fill" color="#FF3B30" />
            <span className="font-head uppercase tracking-wider text-sm font-bold text-white">Pending Approvals</span>
            <span className="ml-1 text-xs font-bold bg-[#FF3B30] text-white rounded-full px-2 py-0.5" data-testid="pending-count">{pending.length}</span>
          </div>
          {pending.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-4 md:px-6 py-4 border-b border-white/5 last:border-0" data-testid={`pending-row-${u.id}`}>
              <Avatar name={u.name} color={roleMeta[u.role]?.color} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{u.name}</div>
                <div className="text-xs text-[#737373] truncate">{u.email}{u.sport ? ` · ${u.sport}` : ""}{u.requested_child_name ? ` · child: ${u.requested_child_name}` : ""}</div>
              </div>
              <RoleBadge role={u.role} />
              <button onClick={() => openApprove(u)} className="inline-flex items-center gap-1 bg-[#34C759] hover:bg-[#2fb350] text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors" data-testid={`approve-user-${u.id}`}><CheckCircle size={16} weight="bold" /> Approve</button>
              <button onClick={() => reject(u)} className="inline-flex items-center gap-1 border border-white/15 text-[#A3A3A3] hover:text-[#FF3B30] hover:border-[#FF3B30]/40 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors" data-testid={`reject-user-${u.id}`}><X size={16} weight="bold" /> Reject</button>
            </div>
          ))}
        </Card>
      )}

      <div className="flex gap-2 mb-6 flex-wrap">
        {["all", ...ROLES].map((r) => (
          <button key={r} onClick={() => setFilter(r)} data-testid={`filter-${r}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold transition-colors ${filter === r ? "bg-[#FF3B30] text-white" : "bg-[#141414] border border-white/10 text-[#A3A3A3] hover:border-white/30"}`}>
            {r === "all" ? "All" : roleMeta[r]?.label || r}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {filtered.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 md:px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
            <Avatar name={u.name} color={roleMeta[u.role]?.color} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate flex items-center gap-2">{u.name}
                {u.role === "athlete" && <EligibilityBadge eligible={u.eligible} testid={`eligibility-badge-${u.id}`} />}
              </div>
              <div className="text-xs text-[#737373] truncate">{u.email}{u.sport ? ` · ${u.sport}` : ""}</div>
            </div>
            <RoleBadge role={u.role} />
            {u.role === "athlete" && (
              <button onClick={() => toggleEligibility(u)} data-testid={`toggle-eligibility-${u.id}`}
                className={`p-2 transition-colors ${u.eligible === false ? "text-[#34C759] hover:text-white" : "text-[#737373] hover:text-[#FF3B30]"}`}
                title={u.eligible === false ? "Mark eligible" : "Mark ineligible"}>
                {u.eligible === false ? <CheckCircle size={18} weight="bold" /> : <Prohibit size={18} weight="bold" />}
              </button>
            )}
            <button onClick={() => openEdit(u)} className="text-[#737373] hover:text-white p-2" data-testid={`edit-user-${u.id}`}><PencilSimple size={18} weight="bold" /></button>
            {u.id !== user?.id && <button onClick={() => resetPassword(u)} className="text-[#737373] hover:text-[#0A84FF] p-2" title="Reset password" data-testid={`reset-password-${u.id}`}><Key size={18} weight="bold" /></button>}
            {u.role !== "athletic_director" && <button onClick={() => del(u.id)} className="text-[#737373] hover:text-[#FF3B30] p-2" data-testid={`delete-user-${u.id}`}><Trash size={18} weight="bold" /></button>}
          </div>
        ))}
        {filtered.length === 0 && <div className="p-8 text-center text-[#737373]">No users in this category.</div>}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Edit Person" : "Add Person"} testid="user-modal">
        {form && (
          <div className="space-y-4">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            {!form.id && <>
              <div><label className={labelCls}>Email</label><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><label className={labelCls}>Temp Password</label><input className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            </>}
            <div>
              <label className={labelCls}>Role</label>
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, role: r })} data-testid={`role-${r}`}
                    className={`py-2 text-[11px] uppercase tracking-wider font-semibold rounded-sm transition-colors ${form.role === r ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}>
                    {roleMeta[r].short}
                  </button>
                ))}
              </div>
            </div>
            {(form.role === "athlete" || form.role === "coach") && (
              <div><label className={labelCls}>Sport</label><input className={inputCls} value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} placeholder="e.g. Basketball" data-testid="user-sport-input" /></div>
            )}
            {(form.role === "athlete" || form.role === "coach") && teams.length > 0 && (
              <div>
                <label className={labelCls}>Teams</label>
                <div className="space-y-2">
                  {teams.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="checkbox" checked={form.team_ids.includes(t.id)} onChange={() => toggle("team_ids", t.id)} data-testid={`team-check-${t.id}`} /> {t.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.role === "parent" && (
              <div>
                <label className={labelCls}>Children (athletes)</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {athletes.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="checkbox" checked={form.child_ids.includes(a.id)} onChange={() => toggle("child_ids", a.id)} data-testid={`child-check-${a.id}`} /> {a.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.role === "teacher" && (
              <div>
                <label className={labelCls}>Students</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {athletes.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="checkbox" checked={form.student_ids.includes(a.id)} onChange={() => toggle("student_ids", a.id)} data-testid={`student-check-${a.id}`} /> {a.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button onClick={save} disabled={saving || !form.name} className={`${btnPrimary} w-full`} data-testid="save-user-button">{saving ? "Saving…" : form.id ? "Save Changes" : "Create Person"}</button>
          </div>
        )}
      </Modal>

      <Modal open={linkOpen} onClose={() => setLinkOpen(false)} title="School Registration Link" testid="registration-link-modal">
        <div className="space-y-4">
          <p className="text-sm text-[#A3A3A3]">Share this single link with everyone at your school. Anyone can register, pick their role (parent, athlete, teacher, or coach), and will appear under <span className="text-white font-semibold">Pending Approvals</span> for you to approve.</p>
          <div className="bg-[#0A0A0A] border border-white/15 rounded-sm p-3 break-all font-mono text-xs text-[#A3A3A3]" data-testid="registration-link-text">{regLink}</div>
          <button onClick={copyLink} className={`${btnPrimary} w-full`} data-testid="copy-registration-link-button">{copied ? <><CheckCircle size={16} weight="bold" /> Copied</> : <><Copy size={16} weight="bold" /> Copy Link</>}</button>
        </div>
      </Modal>

      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset" testid="reset-result-modal">
        {resetResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[#34C759] text-sm font-semibold"><CheckCircle size={18} weight="fill" /> Temporary password created for {resetResult.name}.</div>
            <p className="text-sm text-[#A3A3A3]">Share this with <span className="text-white font-semibold">{resetResult.email}</span>. They'll be required to set a new password on next login.</p>
            <div className="bg-[#0A0A0A] border border-white/15 rounded-sm p-3 break-all font-mono text-lg text-white text-center tracking-widest" data-testid="reset-temp-password">{resetResult.temp_password}</div>
            <button onClick={copyTempPw} className={`${btnPrimary} w-full`} data-testid="copy-temp-password-button">{resetCopied ? <><CheckCircle size={16} weight="bold" /> Copied</> : <><Copy size={16} weight="bold" /> Copy Password</>}</button>
          </div>
        )}
      </Modal>

      <Modal open={!!approve} onClose={() => setApprove(null)} title="Approve Request" testid="approve-modal">
        {approve && (
          <div className="space-y-4">
            <div className="text-sm text-[#A3A3A3]">Approving <span className="text-white font-semibold">{approve.user.name}</span> as <span className="text-white font-semibold">{roleMeta[approve.user.role]?.label || approve.user.role}</span>.</div>
            {approve.user.role === "parent" && (
              <div>
                <label className={labelCls}>Link to Child{approve.user.requested_child_name ? ` (requested: ${approve.user.requested_child_name})` : ""}</label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {athletes.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="checkbox" checked={approve.child_ids.includes(a.id)} onChange={() => toggleApprove("child_ids", a.id)} data-testid={`approve-child-${a.id}`} /> {a.name}
                    </label>
                  ))}
                  {athletes.length === 0 && <div className="text-sm text-[#737373]">No athletes yet — you can link a child later via Edit.</div>}
                </div>
              </div>
            )}
            {(approve.user.role === "athlete" || approve.user.role === "coach") && (
              <>
                <div><label className={labelCls}>Sport</label><input className={inputCls} value={approve.sport} onChange={(e) => setApprove({ ...approve, sport: e.target.value })} placeholder="e.g. Basketball" data-testid="approve-sport-input" /></div>
                {teams.length > 0 && (
                  <div>
                    <label className={labelCls}>Assign to Teams</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {teams.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                          <input type="checkbox" checked={approve.team_ids.includes(t.id)} onChange={() => toggleApprove("team_ids", t.id)} data-testid={`approve-team-${t.id}`} /> {t.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <button onClick={confirmApprove} className={`${btnPrimary} w-full`} data-testid="confirm-approve-button"><CheckCircle size={16} weight="bold" /> Approve &amp; Grant Access</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
